import React, { useEffect, useState } from "react";
import { db, auth } from "../../firebase";
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  onSnapshot,
  getDocs,
  getDoc,
  updateDoc,
  serverTimestamp,
  increment,
  query,
  where,
} from "firebase/firestore";

export default function Groups() {
  const [groups, setGroups] = useState([]);
  const [joinRequests, setJoinRequests] = useState([]);
  const [payments, setPayments] = useState([]);
  const [bids, setBids] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [memberData, setMemberData] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [selectedBid, setSelectedBid] = useState(null);
  const [replyAction, setReplyAction] = useState(null); // 'approve' or 'reject'
  const [replyMessage, setReplyMessage] = useState("");

  // Fetch groups with members - filtered by organizerId
  useEffect(() => {
    if (!auth.currentUser) return;

    const currentUserId = auth.currentUser.uid;
    const groupsQuery = query(collection(db, "groups"), where("organizerId", "==", currentUserId));
    const unsubscribe = onSnapshot(groupsQuery, async (snapshot) => {
      const groupsData = await Promise.all(
        snapshot.docs.map(async (g) => {
          const membersSnap = await getDocs(collection(db, `groups/${g.id}/members`));
          const members = membersSnap.docs.map((m) => ({ id: m.id, ...m.data() }));
          return { id: g.id, ...g.data(), members };
        })
      );
      setGroups(groupsData);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Filter join requests, payments, and bids by organizer's groups
  useEffect(() => {
    if (groups.length === 0) {
      setJoinRequests([]);
      setPayments([]);
      setBids([]);
      return;
    }

    const organizerGroupIds = groups.map((g) => g.id);

    // subscribe to joinRequests
    const unsubReq = onSnapshot(collection(db, "joinRequests"), (snap) => {
      const allRequests = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const filteredRequests = allRequests.filter((req) => 
        organizerGroupIds.includes(req.groupId)
      );
      console.log("📋 Organizer - Join Requests fetched:", filteredRequests.length, "requests");
      console.log("📋 Requests data:", filteredRequests);
      setJoinRequests(filteredRequests);
    });

    // subscribe to payments
    const unsubPayments = onSnapshot(collection(db, "payments"), (snap) => {
      const allPayments = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const filteredPayments = allPayments.filter((p) => 
        organizerGroupIds.includes(p.groupId)
      );
      setPayments(filteredPayments);
    });

    // subscribe to bids
    const unsubBids = onSnapshot(collection(db, "bids"), (snap) => {
      const allBids = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const filteredBids = allBids.filter((b) => 
        organizerGroupIds.includes(b.groupId)
      );
      setBids(filteredBids);
    });

    return () => {
      unsubReq();
      unsubPayments();
      unsubBids();
    };
  }, [groups]);

  // Open reply modal for emergency bid
  const openReplyModal = (bid, action) => {
    setSelectedBid(bid);
    setReplyAction(action);
    setReplyMessage("");
    setShowReplyModal(true);
  };

  // Close reply modal
  const closeReplyModal = () => {
    setShowReplyModal(false);
    setSelectedBid(null);
    setReplyAction(null);
    setReplyMessage("");
  };

  // Submit reply (approve or reject with message)
  const submitBidReply = async () => {
    if (!selectedBid || !replyAction) return;

    try {
      const status = replyAction === "approve" ? "approved" : "rejected";
      const bidRef = doc(db, "bids", selectedBid.id);
      
      // Update bid status with reply message
      await updateDoc(bidRef, { 
        status: status, 
        respondedAt: serverTimestamp(),
        organizerReply: replyMessage.trim() || null,
      });

      // Create notification with reply message
      if (selectedBid.memberId) {
        const defaultMessage = replyAction === "approve" 
          ? `Your emergency request for ${selectedBid.groupName} of ₹${selectedBid.amount}${selectedBid.month ? ` for ${selectedBid.month}` : ''} was approved.`
          : `Your emergency request for ${selectedBid.groupName} of ₹${selectedBid.amount}${selectedBid.month ? ` for ${selectedBid.month}` : ''} was rejected.`;
        
        const fullMessage = replyMessage.trim() 
          ? `${defaultMessage} Organizer's message: ${replyMessage.trim()}`
          : defaultMessage;

        await addDoc(collection(db, "notifications"), {
          to: selectedBid.memberId,
          type: "bid_response",
          message: fullMessage,
          groupId: selectedBid.groupId,
          bidId: selectedBid.id,
          status: status,
          createdAt: serverTimestamp(),
          read: false,
        });
      }

      alert(`✅ Emergency request ${status === "approved" ? "approved" : "rejected"} successfully!`);
      closeReplyModal();
    } catch (err) {
      console.error("Error submitting bid reply:", err);
      alert(`Failed to ${replyAction} bid. Please try again.`);
    }
  };

  // Toggle Add Member form
  const handleAddMemberClick = (groupId) => {
    setSelectedGroup(selectedGroup === groupId ? null : groupId);
    setMemberData({ name: "", email: "", phone: "" });
  };

  // Handle input change
  const handleInputChange = (e) => {
    setMemberData({ ...memberData, [e.target.name]: e.target.value });
  };

  // Add new member to Firestore
  const handleSubmit = async (groupId) => {
    const { name, email, phone } = memberData;

    if (!name || !email || !phone) {
      alert("Please fill all fields");
      return;
    }
    if (!email.includes("@")) {
      alert("Email must contain '@'");
      return;
    }
    if (phone.length !== 10) {
      alert("Phone number must be exactly 10 digits");
      return;
    }

    try {
      // Add member to the members subcollection
      await addDoc(collection(db, `groups/${groupId}/members`), {
        name,
        email,
        phone,
        joinedAt: new Date(),
      });

      // Increment the currentMembers count in the group document
      const groupRef = doc(db, "groups", groupId);
      await updateDoc(groupRef, {
        currentMembers: increment(1),
      });

      alert("✅ Member added successfully! Group member count updated.");
      setSelectedGroup(null);
      // Reset form
      setMemberData({ name: "", email: "", phone: "" });
    } catch (error) {
      console.error("Error adding member:", error);
      alert("Failed to add member. Please try again.");
    }
  };

  // Delete member
  const handleDeleteMember = async (groupId, memberId) => {
    if (window.confirm("Are you sure you want to delete this member?")) {
      try {
        // Get member data to find their memberId before deleting
        const memberRef = doc(db, `groups/${groupId}/members/${memberId}`);
        const memberSnap = await getDoc(memberRef);
        const memberData = memberSnap.data();
        const memberUserId = memberData?.memberId;

        // Delete the member document
        await deleteDoc(doc(db, `groups/${groupId}/members/${memberId}`));
        
        // Decrement the currentMembers count in the group document
        const groupRef = doc(db, "groups", groupId);
        await updateDoc(groupRef, {
          currentMembers: increment(-1),
        });

        // Update joinRequest status to "removed" so member side reflects the removal
        if (memberUserId) {
          const group = groups.find(g => g.id === groupId);
          const joinRequestsSnap = await getDocs(
            query(
              collection(db, "joinRequests"),
              where("groupId", "==", groupId),
              where("memberId", "==", memberUserId)
            )
          );
          
          if (!joinRequestsSnap.empty) {
            const joinRequestDoc = joinRequestsSnap.docs[0];
            await updateDoc(doc(db, "joinRequests", joinRequestDoc.id), {
              status: "removed",
              removedAt: serverTimestamp(),
            });
            console.log("✅ Join request status updated to 'removed'");

            // Send notification to member about removal
            if (group) {
              await addDoc(collection(db, "notifications"), {
                to: memberUserId,
                type: "member_removed",
                message: `You have been removed from the group "${group.groupName}" by the organizer.`,
                groupId: groupId,
                createdAt: serverTimestamp(),
                read: false,
              });
              console.log("✅ Removal notification sent to member");
            }
          }
        }
        
        alert("🗑️ Member deleted successfully! Group member count updated and member notified.");
      } catch (error) {
        console.error("Error deleting member:", error);
        alert("Failed to delete member. Please try again.");
      }
    }
  };

  // Accept join request: add to members, update request status and group counts, notify member
  const acceptRequest = async (request) => {
    try {
      console.log("� ACCEPT BUTTON CLICKED");
      console.log("👤 Accepting request for member:", request.memberName, "ID:", request.memberId);
      console.log("📋 Full request object:", request);
      
      if (!request || !request.id) {
        alert("❌ Invalid request object");
        return;
      }
      
      if (!request.memberId) {
        alert("❌ ERROR: Member ID is missing from request!");
        return;
      }
      
      const group = groups.find((g) => g.id === request.groupId);
      if (!group) {
        console.error("❌ Group not found for ID:", request.groupId);
        alert("❌ Group not found");
        return;
      }

      if (group.maxMembers && (group.currentMembers || 0) >= group.maxMembers) {
        alert("❌ Group is full");
        return;
      }

      // Step 1: Add member to group's members subcollection
      console.log("➕ Step 1: Adding member to group subcollection...");
      await addDoc(collection(db, `groups/${group.id}/members`), {
        name: request.memberName || "Unknown",
        email: request.memberEmail || "unknown@email.com",
        phone: request.memberPhone || "0000000000",
        memberId: request.memberId,
        joinedAt: serverTimestamp(),
        paid: false,
        hasWon: false, // Initialize hasWon to false
      });
      console.log("✅ Step 1 Complete: Member added to group");

      // Step 2: Update join request status
      console.log("🔄 Step 2: Updating request status to 'accepted'...");
      const reqRef = doc(db, "joinRequests", request.id);
      await updateDoc(reqRef, { 
        status: "accepted", 
        respondedAt: serverTimestamp(),
        acceptedAt: serverTimestamp() 
      });
      console.log("✅ Step 2 Complete: Request status updated to accepted");

      // Step 3: Increment currentMembers on group
      console.log("📊 Step 3: Incrementing group member count...");
      const groupRef = doc(db, "groups", group.id);
      await updateDoc(groupRef, { currentMembers: increment(1) });
      console.log("✅ Step 3 Complete: Group currentMembers incremented");

      // Step 4: Send notification to member
      console.log("📧 Step 4: CREATING NOTIFICATION for member UID:", request.memberId);
      const notifDocRef = await addDoc(collection(db, "notifications"), {
        to: request.memberId,
        type: "request_response",
        message: `✅ Organizer accepted your request to join ${group.groupName}!`,
        groupId: group.id,
        createdAt: serverTimestamp(),
        read: false,
      });
      console.log("✅ Step 4 Complete: NOTIFICATION CREATED with ID:", notifDocRef.id);
      console.log("📧 Notification details: to =", request.memberId, ", message =", `Organizer accepted your request to join ${group.groupName}!`);

      alert("✅ SUCCESS!\n✓ Request accepted\n✓ Member added\n✓ Notification sent to member UID: " + request.memberId);
    } catch (err) {
      console.error("❌ CRITICAL ERROR in acceptRequest:", err);
      console.error("Error code:", err.code);
      console.error("Error message:", err.message);
      alert("❌ FAILED TO ACCEPT REQUEST\n\nError: " + err.message + "\n\nCheck browser console (F12) for details!");
    }
  };

  // Reject join request: update status and notify member
  const rejectRequest = async (request) => {
    try {
      if (!request || !request.id) {
        return alert("Invalid request object");
      }

      console.log("❌ Rejecting request:", request.id, "for member:", request.memberId);
      const reqRef = doc(db, "joinRequests", request.id);
      await updateDoc(reqRef, { 
        status: "rejected", 
        respondedAt: serverTimestamp(),
        rejectedAt: serverTimestamp()
      });
      console.log("✅ Request status updated to rejected");

      if (request.memberId) {
        console.log("📧 Creating rejection notification for member:", request.memberId);
        await addDoc(collection(db, "notifications"), {
          to: request.memberId,
          type: "request_response",
          message: `Your request to join ${request.groupName} was rejected.`,
          groupId: request.groupId,
          createdAt: serverTimestamp(),
          read: false,
        });
        console.log("✅ Rejection notification created");
      }

      alert("🔴 Request rejected.");
    } catch (err) {
      console.error("❌ Error rejecting request:", err);
      alert("Failed to reject request. See console for details: " + err.message);
    }
  };

  // derive payments summary by group so `payments` is used and totals render correctly
  const paymentsByGroup = payments.reduce((acc, p) => {
    if (!p || !p.groupId) return acc;
    if (!acc[p.groupId]) acc[p.groupId] = { total: 0, count: 0 };
    acc[p.groupId].total += Number(p.amount || 0);
    if (p.status === "completed") acc[p.groupId].count += 1;
    return acc;
  }, {});

  return (
    <div style={styles.page}>
      <div style={{ marginBottom: '40px' }}>
        <h1 style={{
          fontSize: '32px',
          fontWeight: '700',
          color: '#111827',
          marginBottom: '8px',
          background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}>
          Group Management
        </h1>
        <p style={{ color: '#6b7280', fontSize: '16px', margin: 0 }}>
          Manage your groups, members, and requests
        </p>
      </div>

      <div style={styles.groupList}>
        {groups.map((group) => (
          <div key={group.id} style={styles.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '14px',
                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: '700',
                fontSize: '20px',
              }}>
                {group.groupName ? group.groupName.charAt(0).toUpperCase() : 'G'}
              </div>
              <div style={{ flex: 1 }}>
                <h2 style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#111827',
                  margin: '0 0 4px 0',
                }}>{group.groupName}</h2>
                <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
                  {group.members?.length || 0} / {group.maxMembers || '—'} members
                </p>
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '12px',
              marginBottom: '20px',
            }}>
              <div style={{
                padding: '14px',
                backgroundColor: '#eff6ff',
                borderRadius: '10px',
                border: '1px solid #bfdbfe',
              }}>
                <div style={{ fontSize: '12px', color: '#1e40af', fontWeight: '600', marginBottom: '4px' }}>
                  Contribution
                </div>
                <div style={{ fontSize: '18px', fontWeight: '700', color: '#1e40af' }}>
                  ₹{group.contribution || 0}
                </div>
              </div>
              <div style={{
                padding: '14px',
                backgroundColor: '#f0fdf4',
                borderRadius: '10px',
                border: '1px solid #bbf7d0',
              }}>
                <div style={{ fontSize: '12px', color: '#166534', fontWeight: '600', marginBottom: '4px' }}>
                  Collected
                </div>
                <div style={{ fontSize: '18px', fontWeight: '700', color: '#166534' }}>
                  ₹{paymentsByGroup[group.id]?.total || group.totalCollected || 0}
                </div>
              </div>
              <div style={{
                padding: '14px',
                backgroundColor: '#fef3c7',
                borderRadius: '10px',
                border: '1px solid #fde68a',
              }}>
                <div style={{ fontSize: '12px', color: '#92400e', fontWeight: '600', marginBottom: '4px' }}>
                  Paid Members
                </div>
                <div style={{ fontSize: '18px', fontWeight: '700', color: '#92400e' }}>
                  {paymentsByGroup[group.id]?.count || group.membersPaid || 0}
                </div>
              </div>
              <div style={{
                padding: '14px',
                backgroundColor: '#f3f4f6',
                borderRadius: '10px',
                border: '1px solid #e5e7eb',
              }}>
                <div style={{ fontSize: '12px', color: '#374151', fontWeight: '600', marginBottom: '4px' }}>
                  Remaining
                </div>
                <div style={{ fontSize: '18px', fontWeight: '700', color: '#374151' }}>
                  ₹{((group.maxMembers || group.members?.length || 0) * (group.contribution || 0) - (paymentsByGroup[group.id]?.total || group.totalCollected || 0)) || 0}
                </div>
              </div>
            </div>

            <button
              onClick={() => handleAddMemberClick(group.id)}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: selectedGroup === group.id ? '#6b7280' : '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                marginBottom: '16px',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                if (selectedGroup !== group.id) {
                  e.target.style.backgroundColor = '#1d4ed8';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedGroup !== group.id) {
                  e.target.style.backgroundColor = '#2563eb';
                }
              }}
            >
              {selectedGroup === group.id ? "Cancel" : "+ Add Member"}
            </button>

            {/* Add Member Form */}
            {selectedGroup === group.id && (
              <div style={{
                padding: '20px',
                backgroundColor: '#f9fafb',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                marginBottom: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}>
                <input
                  type="text"
                  name="name"
                  placeholder="Member Name"
                  value={memberData.name}
                  onChange={handleInputChange}
                  style={{
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'all 0.2s',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2563eb';
                    e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={memberData.email}
                  onChange={handleInputChange}
                  style={{
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'all 0.2s',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2563eb';
                    e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <input
                  type="number"
                  name="phone"
                  placeholder="Phone Number"
                  value={memberData.phone}
                  onChange={handleInputChange}
                  style={{
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'all 0.2s',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2563eb';
                    e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <button
                  onClick={() => handleSubmit(group.id)}
                  style={{
                    padding: '12px',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#1d4ed8';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#2563eb';
                  }}
                >
                  Add Member
                </button>
              </div>
            )}

            {/* Members Section */}
            <div style={{
              marginTop: '20px',
              paddingTop: '20px',
              borderTop: '2px solid #e5e7eb',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '700',
                }}>
                  {group.members?.length || 0}
                </div>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#111827',
                  margin: 0,
                }}>Members</h3>
              </div>
              {group.members?.length === 0 ? (
                <div style={{
                  padding: '24px',
                  textAlign: 'center',
                  backgroundColor: '#f9fafb',
                  borderRadius: '10px',
                  border: '2px dashed #e5e7eb',
                }}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>👤</div>
                  <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>No members yet</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {group.members.map((m) => (
                    <div key={m.id} style={{
                      padding: '14px',
                      backgroundColor: '#ffffff',
                      borderRadius: '10px',
                      border: '1px solid #e5e7eb',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#2563eb';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(37, 99, 235, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    >
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: '700',
                          fontSize: '16px',
                        }}>
                          {m.name ? m.name.charAt(0).toUpperCase() : '?'}
                        </div>
                        <div>
                          <p style={{
                            margin: '0 0 4px 0',
                            fontWeight: '600',
                            fontSize: '15px',
                            color: '#111827',
                          }}>{m.name}</p>
                          <p style={{
                            margin: 0,
                            fontSize: '13px',
                            color: '#6b7280',
                          }}>
                            {m.email} • {m.phone}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteMember(group.id, m.id)}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#dc2626',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: '600',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#b91c1c';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#dc2626';
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pending Join Requests */}
            <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '2px solid #e5e7eb' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '700',
                }}>
                  {joinRequests.filter(r => r.groupId === group.id && (r.status === 'pending' || r.status === 'processing')).length}
                </div>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#111827',
                  margin: 0,
                }}>Join Requests</h3>
              </div>
              {joinRequests.filter(r => r.groupId === group.id && (r.status === 'pending' || r.status === 'processing')).length === 0 ? (
                <p style={{ color: '#777', fontSize: 14 }}>No pending requests.</p>
              ) : (
                joinRequests
                  .filter(r => r.groupId === group.id && (r.status === 'pending' || r.status === 'processing'))
                  .map((req) => (
                    <div key={req.id} style={{
                      padding: '14px',
                      backgroundColor: '#fffbeb',
                      borderRadius: '10px',
                      border: '1px solid #fde68a',
                      marginBottom: '10px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}>
                      <div style={{ flex: 1 }}>
                        <p style={{
                          margin: '0 0 4px 0',
                          fontWeight: '600',
                          fontSize: '15px',
                          color: '#111827',
                        }}>{req.memberName || 'Unnamed'}</p>
                        <p style={{
                          margin: '0 0 4px 0',
                          fontSize: '13px',
                          color: '#6b7280',
                        }}>
                          {new Date((req.createdAt?.seconds || 0) * 1000).toLocaleDateString()}
                        </p>
                        <span style={{
                          padding: '2px 8px',
                          backgroundColor: req.status === 'processing' ? '#dbeafe' : '#fef3c7',
                          color: req.status === 'processing' ? '#1e40af' : '#92400e',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: '600',
                        }}>
                          {req.status}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={() => acceptRequest(req)} style={{
                          padding: '8px 16px',
                          backgroundColor: '#2563eb',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: '600',
                        }}>
                          Accept
                        </button>
                        <button onClick={() => rejectRequest(req)} style={{
                          padding: '8px 16px',
                          backgroundColor: '#dc2626',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: '600',
                        }}>
                          Reject
                        </button>
                      </div>
                    </div>
                  ))
              )}
            </div>

            {/* Pending Bids */}
            <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '2px solid #e5e7eb' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '700',
                }}>
                  {bids.filter(b => b.groupId === group.id && b.status === 'pending').length}
                </div>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#111827',
                  margin: 0,
                }}>Emergency Requests</h3>
              </div>
              {bids.filter(b => b.groupId === group.id && b.status === 'pending').length === 0 ? (
                <p style={{ color: '#777', fontSize: 14 }}>No pending emergency requests.</p>
              ) : (
                bids.filter(b => b.groupId === group.id && b.status === 'pending').map(bid => (
                  <div key={bid.id} style={{ 
                    ...styles.memberCard, 
                    borderLeft: '4px solid #f59e0b',
                    backgroundColor: '#fffbeb',
                    padding: '16px',
                    marginBottom: '12px',
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <p style={{ ...styles.memberName, margin: 0, fontSize: '16px', fontWeight: '600' }}>
                          {bid.memberName || 'Unnamed Member'}
                        </p>
                        <span style={{ 
                          fontSize: '11px', 
                          padding: '2px 8px', 
                          backgroundColor: '#fef3c7', 
                          color: '#92400e',
                          borderRadius: '12px',
                          fontWeight: '600'
                        }}>
                          PENDING
                        </span>
                      </div>
                      <div style={{ marginBottom: '8px' }}>
                        <p style={{ margin: '4px 0', fontSize: '14px', color: '#111827' }}>
                          <strong>Amount:</strong> ₹{bid.amount}
                        </p>
                        {bid.month && (
                          <p style={{ margin: '4px 0', fontSize: '14px', color: '#111827' }}>
                            <strong>For Month:</strong> {bid.month}
                          </p>
                        )}
                        <p style={{ margin: '4px 0', fontSize: '14px', color: '#111827' }}>
                          <strong>Reason:</strong> {bid.reason}
                        </p>
                        {bid.createdAt && (
                          <p style={{ margin: '4px 0', fontSize: '12px', color: '#6b7280' }}>
                            Requested: {
                              bid.createdAt.toDate 
                                ? new Date(bid.createdAt.toDate()).toLocaleDateString()
                                : bid.createdAt.seconds
                                ? new Date(bid.createdAt.seconds * 1000).toLocaleDateString()
                                : 'N/A'
                            }
                          </p>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: '12px' }}>
                      <button 
                        onClick={() => openReplyModal(bid, 'approve')} 
                        style={{ 
                          ...styles.submitBtn, 
                          backgroundColor: '#059669',
                          padding: '10px 20px',
                          fontSize: '14px',
                          fontWeight: '600',
                        }}
                      >
                        ✓ Approve
                      </button>
                      <button 
                        onClick={() => openReplyModal(bid, 'reject')} 
                        style={{ 
                          ...styles.deleteBtn,
                          padding: '10px 20px',
                          fontSize: '14px',
                          fontWeight: '600',
                        }}
                      >
                        ✗ Reject
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Reply Modal for Emergency Requests */}
      {showReplyModal && selectedBid && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}
          onClick={closeReplyModal}
        >
          <div 
            style={{
              backgroundColor: '#fff',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 10px 30px rgba(2,6,23,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', margin: 0 }}>
                {replyAction === 'approve' ? '✓ Approve' : '✗ Reject'} Emergency Request
              </h2>
              <button
                onClick={closeReplyModal}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f3f4f6';
                  e.target.style.color = '#111827';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#6b7280';
                }}
              >
                ×
              </button>
            </div>

            {/* Request Details */}
            <div style={{ 
              marginBottom: '24px', 
              padding: '16px', 
              backgroundColor: replyAction === 'approve' ? '#ecfdf5' : '#fef2f2', 
              borderRadius: '8px', 
              border: `1px solid ${replyAction === 'approve' ? '#a7f3d0' : '#fecaca'}` 
            }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                Member: {selectedBid.memberName || 'Unnamed'}
              </p>
              <p style={{ margin: '4px 0', fontSize: '14px', color: '#111827' }}>
                <strong>Amount:</strong> ₹{selectedBid.amount}
              </p>
              {selectedBid.month && (
                <p style={{ margin: '4px 0', fontSize: '14px', color: '#111827' }}>
                  <strong>For Month:</strong> {selectedBid.month}
                </p>
              )}
              <p style={{ margin: '4px 0', fontSize: '14px', color: '#111827' }}>
                <strong>Reason:</strong> {selectedBid.reason}
              </p>
            </div>

            {/* Reply Message */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
                Your Reply Message {replyAction === 'approve' ? '(Optional)' : '(Recommended)'}
              </label>
              <textarea
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                placeholder={replyAction === 'approve' 
                  ? "Add a message to the member (optional). Example: 'Your request has been approved. Funds will be transferred within 2-3 business days.'"
                  : "Please provide a reason for rejection (recommended). Example: 'We cannot approve this request at this time due to...'"}
                rows="5"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  transition: 'all 0.2s',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#2563eb';
                  e.target.style.outline = 'none';
                  e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
                {replyMessage.length} characters
              </p>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '28px' }}>
              <button
                onClick={closeReplyModal}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  border: '1px solid #e5e7eb',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#e5e7eb';
                  e.target.style.color = '#111827';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#f3f4f6';
                  e.target.style.color = '#374151';
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitBidReply}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  border: 'none',
                  backgroundColor: replyAction === 'approve' ? '#059669' : '#dc2626',
                  color: '#fff',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = replyAction === 'approve' ? '#047857' : '#b91c1c';
                  e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = replyAction === 'approve' ? '#059669' : '#dc2626';
                  e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                }}
              >
                {replyAction === 'approve' ? '✓ Confirm Approval' : '✗ Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    backgroundColor: "#f8fafc",
    minHeight: "100vh",
    padding: "32px",
    fontFamily: "Segoe UI, sans-serif",
    marginLeft: "220px",
  },
  heading: {
    textAlign: "center",
    color: "#222",
    fontSize: "34px",
    fontWeight: "600",
    marginBottom: "40px",
  },
  groupList: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
    gap: "24px",
  },
  card: {
    background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
    borderRadius: "16px",
    padding: "24px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    border: "1px solid #e5e7eb",
    transition: "all 0.3s ease",
  },
  groupTitle: {
    fontSize: "22px",
    color: "#007bff",
    marginBottom: "8px",
  },
  groupMeta: {
    color: "#666",
    fontSize: "15px",
    marginBottom: "15px",
    lineHeight: "1.5",
  },
  addBtn: {
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    padding: "10px 18px",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "bold",
    marginBottom: "10px",
  },
  formBox: {
    marginTop: "15px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "10px",
    backgroundColor: "#f1f3f6",
    borderRadius: "8px",
  },
  input: {
    padding: "10px",
    border: "1px solid #ccc",
    borderRadius: "6px",
    fontSize: "15px",
  },
  submitBtn: {
    backgroundColor: "#28a745",
    color: "white",
    border: "none",
    padding: "10px",
    borderRadius: "6px",
    fontWeight: "bold",
    cursor: "pointer",
  },
  membersBox: {
    marginTop: "20px",
    background: "#f8f9fb",
    padding: "12px",
    borderRadius: "10px",
  },
  memberHeading: {
    fontSize: "17px",
    color: "#333",
    marginBottom: "10px",
  },
  memberCard: {
    background: "white",
    padding: "10px 15px",
    borderRadius: "8px",
    marginBottom: "10px",
    borderLeft: "4px solid #007bff",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  memberName: {
    fontWeight: "bold",
    margin: 0,
  },
  memberInfo: {
    fontSize: "14px",
    color: "#555",
  },
  deleteBtn: {
    backgroundColor: "#dc3545",
    color: "white",
    border: "none",
    padding: "6px 12px",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "bold",
  },
  noMember: {
    fontSize: "14px",
    color: "#777",
  },
};
